import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";

const ACCEPT_STATIC = "image/webp,image/jpeg,image/png,application/pdf";
const MAX_STATIC_MB = 10;

function resolveMediaType(file) {
  if (!file) return null;
  if (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf")) {
    return "pdf";
  }
  return "image";
}

const StepStaticImport = ({
  draft,
  setDraft,
  goNext,
  goPrev,
  staticMenuFile,
  setStaticMenuFile,
  staticMenuPreview,
  setStaticMenuPreview,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const mediaType = draft?.menu?.staticMenuMediaType || resolveMediaType(staticMenuFile);
  const canNext = useMemo(() => !!staticMenuFile || !!staticMenuPreview, [staticMenuFile, staticMenuPreview]);

  const applyFile = (selectedFile, inputEl) => {
    if (!selectedFile) return;

    const maxBytes = MAX_STATIC_MB * 1024 * 1024;
    if (selectedFile.size > maxBytes) {
      swal(t("warning"), t("wizardStaticFileTooLarge", { max: MAX_STATIC_MB }), "warning");
      if (inputEl) inputEl.value = "";
      return;
    }

    const allowed = ACCEPT_STATIC.split(",");
    if (!allowed.includes(selectedFile.type) && !selectedFile.name?.toLowerCase().endsWith(".pdf")) {
      swal(t("warning"), t("menuStaticFileHint"), "warning");
      if (inputEl) inputEl.value = "";
      return;
    }

    if (staticMenuPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticMenuPreview);
    }

    setStaticMenuFile(selectedFile);
    setStaticMenuPreview(URL.createObjectURL(selectedFile));
    setDraft((prev) => ({
      ...prev,
      menu: {
        ...prev.menu,
        staticMenuMediaType: resolveMediaType(selectedFile),
      },
    }));
  };

  const handleFileChange = (event) => {
    applyFile(event.target.files?.[0], event.target);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    applyFile(event.dataTransfer.files?.[0]);
  };

  const handleRemove = () => {
    if (staticMenuPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticMenuPreview);
    }
    setStaticMenuFile(null);
    setStaticMenuPreview(null);
    setDraft((prev) => ({
      ...prev,
      menu: {
        ...prev.menu,
        staticMenuMediaType: null,
      },
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const previewUrl = staticMenuPreview;
  const fileLabel = staticMenuFile?.name || (mediaType === "pdf" ? "PDF" : t("wizardStaticImportHeading"));

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">3</span>
            {t("wizardStaticImportHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardStaticImportSub")}</p>
        </div>

        <div className="start-static">
          {previewUrl ? (
            <div className="start-static-preview">
              <div className="start-static-preview__frame">
                {mediaType === "pdf" ? (
                  <iframe
                    title={t("menuContentTypeStatic")}
                    src={previewUrl}
                    className="start-static-preview__pdf"
                  />
                ) : (
                  <img src={previewUrl} alt="" className="start-static-preview__image" />
                )}
              </div>
              <div className="start-static-preview__meta">
                <span className="start-static-preview__name">{fileLabel}</span>
                <span className="start-static-preview__hint">{t("wizardStaticImportFormats")}</span>
                <div className="start-static-preview__actions">
                  <button
                    type="button"
                    className="start-static-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("change")}
                  </button>
                  <button type="button" className="start-static-btn start-static-btn--danger" onClick={handleRemove}>
                    {t("remove")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={`start-static-dropzone${dragOver ? " is-dragover" : ""}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <span className="start-static-dropzone__icon" aria-hidden="true">
                <i className="fa fa-cloud-upload-alt" />
              </span>
              <span className="start-static-dropzone__title">{t("menuStaticUploadButton")}</span>
              <span className="start-static-dropzone__hint">{t("wizardStaticImportFormats")}</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={ACCEPT_STATIC}
            onChange={handleFileChange}
          />
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStepStaticImportCounter")}</div>
        <button
          className={`btn btn-primary sw-btn-next ms-1 start-btn-next ${!canNext ? "disabled" : ""}`}
          disabled={!canNext}
          onClick={goNext}
          type="button"
        >
          {t("wizardContinue")} <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
};

export default StepStaticImport;
